import type { Store } from '../game/state';
import { DIFFICULTY_ORDER, DIFFICULTY_LABEL } from '../game/grid';
import type { DifficultyLevel } from '../game/grid';
import type { GameActions } from './toolbar';

/** 设置弹层：收纳难度 / 旋转 / 参考图 / 音效 / 主题 / 分享，工具栏只留核心状态与操作。 */
export interface Settings {
  el: HTMLElement;
  open(): void;
  close(): void;
}

export function createSettings(actions: GameActions, store: Store): Settings {
  const el = document.createElement('div');
  el.className = 'pintu-overlay pintu-settings';
  el.hidden = true;
  el.innerHTML = `
    <div class="pintu-card pintu-settings-card" role="dialog" aria-modal="true" aria-label="设置">
      <button type="button" class="pintu-close" data-close aria-label="关闭">×</button>
      <h3>设置</h3>
      <div class="pintu-settings-row">
        <span>难度</span>
        <div class="pintu-seg">
          ${DIFFICULTY_ORDER.map((lv) => `<button type="button" data-diff="${lv}">${DIFFICULTY_LABEL[lv]}</button>`).join('')}
        </div>
      </div>
      <div class="pintu-settings-row">
        <span>旋转模式</span>
        <label class="pintu-switch"><input type="checkbox" data-rotate/><i></i></label>
      </div>
      <div class="pintu-settings-row">
        <span>参考图</span>
        <label class="pintu-switch"><input type="checkbox" data-ref/><i></i></label>
      </div>
      <div class="pintu-settings-row">
        <span>音效</span>
        <button type="button" data-mute class="pintu-settings-toggle"></button>
      </div>
      <div class="pintu-settings-row">
        <span>主题</span>
        <div class="pintu-theme-btns">
          <button type="button" data-theme="cozy">木纹</button>
          <button type="button" data-theme="dark">夜间</button>
          <button type="button" data-theme="light">亮色</button>
        </div>
      </div>
      <div class="pintu-settings-row">
        <button type="button" data-share class="pintu-settings-share">🔗 复制分享链接</button>
      </div>
    </div>
  `;

  const muteBtn = el.querySelector<HTMLButtonElement>('[data-mute]')!;
  const shareBtn = el.querySelector<HTMLButtonElement>('[data-share]')!;
  const closeBtn = el.querySelector<HTMLButtonElement>('[data-close]')!;
  const rotate = el.querySelector<HTMLInputElement>('[data-rotate]')!;
  const ref = el.querySelector<HTMLInputElement>('[data-ref]')!;

  const close = (): void => {
    el.hidden = true;
  };
  muteBtn.addEventListener('click', () => actions.toggleMute());
  shareBtn.addEventListener('click', () => actions.share());
  closeBtn.addEventListener('click', close);
  el.addEventListener('click', (e) => {
    if (e.target === el) close();
  });
  rotate.addEventListener('change', () => actions.toggleRotate(rotate.checked));
  ref.addEventListener('change', () => actions.toggleReference(ref.checked));
  el.querySelectorAll<HTMLButtonElement>('[data-diff]').forEach((b) => {
    b.addEventListener('click', () => actions.setDifficulty(b.dataset.diff as DifficultyLevel));
  });
  el.querySelectorAll<HTMLButtonElement>('[data-theme]').forEach((b) => {
    b.addEventListener('click', () =>
      actions.setTheme(b.dataset.theme as 'cozy' | 'dark' | 'light'),
    );
  });

  const update = (): void => {
    const s = store.get();
    muteBtn.textContent = s.muted ? '🔇 已静音' : '🔊 已开启';
    rotate.checked = s.config.rotateMode;
    ref.checked = s.config.showReference;
    el.querySelectorAll<HTMLButtonElement>('[data-diff]').forEach((b) => {
      b.classList.toggle('active', b.dataset.diff === s.config.difficulty);
    });
    el.querySelectorAll<HTMLButtonElement>('[data-theme]').forEach((b) => {
      b.classList.toggle('active', b.dataset.theme === s.theme);
    });
  };
  store.subscribe(update);
  update();

  return {
    el,
    open() {
      el.hidden = false;
    },
    close,
  };
}
